import { ownerApi } from './ownerClient'

interface VapidKeyResponse {
  publicKey: string
}

// Cap the push calls so the alerts toggle can never hang on "Working…" if the backend stalls.
const PUSH_TIMEOUT_MS = 15000

/** The server's VAPID public key (base64url) — passed to pushManager.subscribe as applicationServerKey. */
export async function getVapidPublicKey(): Promise<string> {
  const { data } = await ownerApi.get<VapidKeyResponse>('/push/vapid-public-key', { timeout: PUSH_TIMEOUT_MS })
  return data.publicKey
}

/**
 * Persists a browser subscription server-side. `PushSubscription.toJSON()` yields
 * `{ endpoint, expirationTime, keys: { p256dh, auth } }`; the backend reads endpoint + keys
 * and ignores the rest. The owning staff user + tenant are derived server-side (never sent).
 */
export async function subscribePush(subscription: PushSubscription): Promise<void> {
  await ownerApi.post('/push/subscribe', subscription.toJSON(), { timeout: PUSH_TIMEOUT_MS })
}

/** Drops a subscription server-side by its endpoint (tenant-scoped). */
export async function unsubscribePush(endpoint: string): Promise<void> {
  await ownerApi.post('/push/unsubscribe', { endpoint }, { timeout: PUSH_TIMEOUT_MS })
}

/** Fires a test push to all of this tenant's subscriptions; returns how many it dispatched to. */
export async function sendTestPush(): Promise<number> {
  const { data } = await ownerApi.post<{ sent: number }>('/push/test', undefined, { timeout: PUSH_TIMEOUT_MS })
  return data.sent
}

/**
 * Decode a base64url VAPID key into the Uint8Array that pushManager.subscribe expects as
 * applicationServerKey (the Push API requires a binary key, not the string).
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  // Back the array with an explicit ArrayBuffer so its type is Uint8Array<ArrayBuffer>,
  // which `applicationServerKey: BufferSource` accepts (TS 5.7+ Uint8Array is buffer-generic).
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}
